import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { groupBy, sortBy } from '@libs/array';

import { TournamentParticipantsProps } from '.';

import { useAuth } from '@features/auth/hooks/useAuth';
import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { useClubCardQuery } from '@features/club/api/hooks';
import { useUserSocket } from '@features/shared/api';
import { QUERY_KEYS, useActiveTournamentParticipantsQuery } from '@features/tournament/api/hooks';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

import { TournamentParticipants } from './TournamentParticipants';

export function TournamentParticipantsWidget({
  className,
  hideTitle,
  initialTab = 'NumberAsc',
  alwaysShowNumbers,
}: {
  className?: string;
  hideTitle?: boolean;
  initialTab?: TournamentParticipantsProps['tab'];
  alwaysShowNumbers?: boolean;
}) {
  const { state } = useTournamentCtx();
  const { tournament, clubId, tournamentId } = state;

  const { isManager } = useClubRoles(clubId);
  const { user } = useAuth();

  const [tab, setTab] = useState<TournamentParticipantsProps['tab']>(initialTab);

  const { data: club } = useClubCardQuery(clubId, { staleTime: Infinity });

  const participantsQuery = useActiveTournamentParticipantsQuery(
    clubId,
    tournamentId,
    tab,
    isManager,
  );

  // Отслеживание изменений в участниках
  const qc = useQueryClient();
  useUserSocket((e) => {
    switch (e.eventType) {
      case 'ParticipantsUpdated':
        if (e.tournamentId === tournamentId) {
          qc.invalidateQueries({
            queryKey: QUERY_KEYS.tournamentParticipants(clubId, tournamentId),
          });
          qc.invalidateQueries({
            queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
          });
        }
        break;
    }
  });

  // FIXME: не бэке бак с сортировкой внутри команд
  const participants = useMemo(() => {
    if (tab === 'NumberAsc' && tournament?.teams && participantsQuery.data) {
      const gs = groupBy(participantsQuery.data, (p) => p.teamId || '');
      Object.entries(gs).forEach(([teamId, ps]) => {
        sortBy(ps, (p) => (teamId ? p.numberInTeam : p.numberInTournament) ?? Infinity);
      });
      // sic: порядок команд не важен, поэтому просто склеиваем все группы
      return Object.values(gs).flatMap((ps) => ps);
    }
    return participantsQuery.data;
  }, [participantsQuery.data, tab, tournament?.teams]);

  return (
    <TournamentParticipants
      className={className}
      isPending={participantsQuery.isPending}
      participants={participants}
      tab={tab}
      hideNumbers={!alwaysShowNumbers && tab !== 'NumberAsc'}
      onTabChange={setTab}
      highlightedUserId={user?.userId}
      teams={tournament?.teams}
      isManager={isManager}
      clockControl={tournament?.clockControl}
      showNumberInTeam={tournament?.teamModeSettings?.numberInTeamMode === 'NoShift'}
      hideTitle={hideTitle}
      groupTitle={club?.groupTitle ?? undefined}
      isRcf={!!tournament?.rcfRankCalculated}
      renderShieldQuickPeek={(userId: string) => <PlayerShieldQuickPeekWidget userId={userId} />}
    />
  );
}
